// SCRUM-791 — Clinical Reconciliation (b)(2). App-facing view models for the `api/ccda-receive` feature.
// The wire enums + DTOs now live in @medone/medonehp-api-client (regenerated 2026-07-09). We re-export the
// enums and keep plain, string-dated view models here; api.ts maps the generated (optional-field,
// moment.Moment-dated) DTOs onto these, keeping the Redux store serializable and component shapes stable.
import { MatchStatus, ReconcileDecision, ReconcileListType } from '@medone/medonehp-api-client';

// Wire enums are numeric; the generated client is the single source of truth. (Genders = Male 0 / Female 1 / Unknown 2 / AskedDeclined 3.)
export { Genders as SourceGender, MatchStatus, ReconcileDecision, ReconcileListType } from '@medone/medonehp-api-client';

// `status` is a plain string on the wire; this union is the subset the UI handles. Invalid / Incomplete are the
// SCRUM-924 receive-time negative statuses (blocked from import; never reconcilable). PendingPatientCreation is the
// SCRUM-988 parked state: the document describes a person with no chart yet (still resolvable later).
export type CcdaReceiveStatus = 'PendingMatch' | 'PendingReconciliation' | 'PendingPatientCreation' | 'Reconciled' | 'Rejected' | 'Invalid' | 'Incomplete';

export interface CcdaReceiveListItemDto {
    stagingId: number;
    status: CcdaReceiveStatus;
    receivedDate: string | null;
    documentType: string;
    matchesFound: number;
    suggestedPatientId: number | null;
    resolvedPatientId: number | null;
    // Demographics FROM THE DOCUMENT (not the chart) — lets an unresolved row render without re-parsing.
    sourceFirstName: string | null;
    sourceLastName: string | null;
    sourceDateOfBirth: string | null;
    sourceGender: number | null;
    // Sender identity (SCRUM-921). senderName is the document's source label (Composition author/custodian +
    // title). The Direct address + transport come from the receive envelope: the SCRUM-922 mailbox fetch records
    // the message's From address and transport "IMAP"; an admin import records what the operator typed (transport
    // defaults to "ManualUpload"). Null when nothing supplied them.
    senderName: string | null;
    senderDirectAddress: string | null;
    transport: string | null;
    // Global inbound admin queue enrichment (from the resolved patient's active intake). Null for the
    // per-patient tab and for unmatched rows.
    resolvedPatientName: string | null;
    facilityName: string | null;
    providerName: string | null;
    // SCRUM-924 receive-time validation outcome: a fixed reason for a blocked (Invalid / Incomplete) row, and the
    // detected C-CDA release (R1.1 / R2.1) on every row.
    failureReason: string | null;
    ccdaVersion: string | null;
    // Whether received.xml was retained — the View action keys on this, not on status (a Tier-1 Invalid keeps no
    // body; a transform-failed Invalid and an Incomplete keep the document).
    hasDocument: boolean;
    // SCRUM-1298: receive-time findings recorded on the row (conformance + vocabulary), non-zero on a displayable
    // PendingMatch document too. Key the findings count / Findings action; the detail is fetchValidationReport.
    errorCount: number;
    warningCount: number;
    // matchedDate is when the current binding to a chart was made (null when unbound, or reconciled before it was
    // recorded) and comes on both screens; matchedByName and providerTin are filled for the per-patient tab only.
    // providerTin is the TIN attributed at the FIRST match (or at commit, if the match set none) and is never
    // re-stamped, so after a re-match by someone else it can belong to a different user.
    matchedByName: string | null;
    matchedDate: string | null;
    providerTin: string | null;
}

// SCRUM-1298 — one recorded finding. code / severity / category are the backend enum names (CcdaReceiveErrorCode,
// CcdaReceiveSeverity, CcdaFindingCategory); message is a fixed, code-derived sentence; elementPath is an XPath of
// element names and positions locating it in the document; ruleId is the HL7 C-CDA CONF number when there is one.
export interface CcdaValidationFindingDto {
    code: string;
    severity: string;
    category: string;
    message: string;
    elementPath: string | null;
    ruleId: string | null;
    // The value the rule fixes (a code, a code system OID, a template root) — a constant of the rule, never document content.
    expected: string | null;
}

// SCRUM-1298 — the per-document validation report, read from the stored findings (never recomputed).
export interface CcdaReceiveValidationReportDto {
    stagingId: number;
    status: CcdaReceiveStatus;
    ccdaVersion: string | null;
    failureReason: string | null;
    errorCount: number;
    warningCount: number;
    findings: CcdaValidationFindingDto[];
}

// Server-side paging state for the global inbound queue (mirrors the antd Table pagination shape).
export interface CcdaInboxPagination {
    current: number;
    pageSize: number;
    total: number;
}

// Server-side filters for the global inbound queue. Facility/provider apply to the resolved patient's active
// intake; patientName matches the resolved patient OR the document's source snapshot (so unmatched rows stay findable).
// No status field: the status filter UI was cut by the stakeholder before SCRUM-921 landed, so the Portal
// deliberately does not exercise the API's status filter (which remains available to API consumers/QA).
export interface CcdaInboxFilter {
    patientName?: string | null;
    facilityId?: number | null;
    providerId?: string | null;
    // Date of birth (YYYY-MM-DD) — matched against the resolved patient's DOB OR the document's source DOB.
    dateOfBirth?: string | null;
}

// --- SCRUM-988 Patient Resolution (manual match) view models ---------------------------------------------------
// The `document` side is who the C-CDA says the patient is; `candidates` are live chart matches recomputed on every
// call. gender/sexAtBirth are numeric enums on the wire (Genders / SexAtBirths) and are mapped to display labels in
// api.ts. PHI-heavy — a multi-candidate payload carries several patients' demographics, so it is never persisted in
// Redux (fetched into component-local state and re-read on reload, per AC-5).

// Fixed audit codes for marking a document unmatched (no free text — the audit payload stays codes-only).
export type CcdaUnmatchedReason = 'NotOurPatient' | 'AmbiguousCandidates' | 'DuplicateDocument' | 'Other';

// SCRUM-1515 — why the record reached this clinician, stated at Confirm match. (g)(2) RT15 counts only the first three.
export type CcdaReceiptReason = 'TransitionOfCare' | 'Referral' | 'NewPatient' | 'None';

// The sender's view of the patient. When fromDocumentBundle is false the FHIR bundle was missing/unreadable, so
// only name/DOB/gender are populated (mrn/address/phone are null) — render as "limited detail", not "no MRN".
export interface CcdaResolutionDocumentView {
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
    dateOfBirth: string | null;
    gender: string | null;
    // The SENDER's record number — display only, NEVER compared to our hospitalMrn.
    mrn: string | null;
    addressLine: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
    phone: string | null;
    fromDocumentBundle: boolean;
}

// SCRUM-1477 match tier. FullMatch (4 of 4) / PartialMatch (3 of 4) offer candidates; LowConfidence (2 of 4) and
// Unmatched (0-1) offer none, so the manual search is the way forward. NotEvaluated = matching never ran (a document
// blocked at receive-time validation, which the endpoint reports as such); api.ts also maps an absent or unrecognised
// tier to it.
export type CcdaMatchTier = 'FullMatch' | 'PartialMatch' | 'LowConfidence' | 'Unmatched' | 'NotEvaluated';

// Which of the four matching criteria a candidate agrees with the document on (exact, case- and
// whitespace-insensitive), and how many — so a partial match shows what differs.
export interface CcdaMatchCriteriaView {
    firstName: boolean;
    lastName: boolean;
    dateOfBirth: boolean;
    gender: boolean;
    count: number;
}

// A live chart candidate for the document. hasActiveIntake=false ⇒ it cannot be selected (resolution needs an open
// intake). isSuggested is the LIVE suggestion — the one chart with the best current score when it meets 3 or 4
// criteria, false for all on a tie — and is what the screen tags and pre-selects; isReceiveTimeSuggestion only records
// what was suggested at receipt, which later chart changes can overtake. Binding always takes the explicit confirm.
export interface CcdaResolutionCandidateView {
    patientId: number;
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
    dateOfBirth: string | null;
    gender: string | null;
    sexAtBirth: string | null;
    // OUR MRN (the newest intake's HospitalMRN) — a different identifier space from the document's mrn.
    hospitalMrn: string | null;
    mbi: string | null;
    hasActiveIntake: boolean;
    facilityName: string | null;
    isReceiveTimeSuggestion: boolean;
    isSuggested: boolean;
    matchedCriteria: CcdaMatchCriteriaView;
}

export interface CcdaPatientResolutionView {
    stagingId: number;
    status: CcdaReceiveStatus;
    receivedDate: string | null;
    documentType: string;
    senderName: string | null;
    senderDirectAddress: string | null;
    suggestedPatientId: number | null;
    resolvedPatientId: number | null;
    // The candidate count recorded AT RECEIPT — provenance only; render the list from `candidates`, never this.
    receiveTimeMatchesFound: number;
    document: CcdaResolutionDocumentView | null;
    candidates: CcdaResolutionCandidateView[];
    // The LIVE tier, scored alongside `candidates` — not the one recorded at receipt.
    matchTier: CcdaMatchTier;
    // false ⇒ render the whole screen read-only (a UX affordance; every write re-asserts it server-side).
    canResolve: boolean;
    // Optimistic-concurrency stamp (base64) echoed on mark-new-patient / mark-unmatched; a stale value ⇒ 409 (reload).
    rowVersion: string | null;
}

// The outcome of any of the three resolution actions — the staging row's new status (+ the bound patient on confirm).
// Confirm is the ONLY writer of the resolved patient, and every reconciliation read/write is gated on it, so a
// document that was matched but never confirmed 409s on all of them.
export interface CcdaResolutionOutcome {
    stagingId: number;
    status: CcdaReceiveStatus;
    // Absent on mark-new-patient / mark-unmatched (no chart is bound); coalesce before assigning to a row.
    resolvedPatientId?: number | null;
}

// Outcome of an admin ingest (SCRUM-1171). Identifiers, codes and counts only — the DTO carries no
// demographics by design, which is what makes it safe to render and to capture into the cert evidence bundle.
export interface CcdaIngestResultView {
    stagingId: number;
    status: CcdaReceiveStatus;
    ccdaVersion: string | null;
    matchesFound: number;
    // Set only on a single exact match — a suggestion, never a merge.
    suggestedPatientId: number | null;
    // Fixed, code-derived summary when the receive-time gate blocked the document (SCRUM-924); null otherwise.
    failureReason: string | null;
    errorCount: number;
}

// One received-document audit entry (SCRUM-921 AC6 / §170.315(d)(2)/(d)(3)) — sender Direct address, doc type, high-precision timestamp.
export interface CcdaReceiveAuditEntryDto {
    stagingId: number;
    eventType: string;
    timestamp: string | null;
    userId: string;
    userName: string | null;
    documentType: string | null;
    senderName: string | null;
    senderDirectAddress: string | null;
    senderTransport: string | null;
    // SCRUM-924: the fixed classification of a blocked receive — populated only on a Receive Failed entry.
    failureReason: string | null;
}

// Per-send SMTP credential override. The b.1 ETT send authenticates with the ETT's published shared account,
// which is the configured default; the override lets one send try a different SMTP account in an edge run
// without a configuration redeploy between runs.
//
// Both fields are TRANSIENT by contract: they travel on one request, override the configured account for
// that one send, and are never persisted server-side and never logged. Nothing in the Portal may widen that
// — no Redux state beyond the in-flight thunk arg, no storage, no telemetry, no error/alert text. Omit a
// field (undefined) to keep the configured value; an empty STRING would override it with nothing and fail
// authentication in a way that reads as a bad credential.
export interface CcdaTransmitSmtpOverride {
    smtpUserName?: string;
    smtpPassword?: string;
}

// Outcome of one transmit attempt (SCRUM-927). `outcome` is the ATTEMPT's result while
// `transmissionStatus` is the parent document's status afterwards — they deliberately disagree on a
// suppressed attempt (Suppressed / NotSent), because a suppressed attempt never moves the document.
export interface CcdaTransmitResultDto {
    ccdaDocumentId: number;
    ccdaTransmissionId: number;
    // Sent | Failed | Suppressed | Indeterminate
    outcome: string;
    // The document's CcdaTransmissionStatuses value after the attempt (NotSent | AcceptedByHisp | Sent | Failed).
    // A successful send lands AcceptedByHisp and a failed one Failed; a suppressed attempt leaves it untouched.
    // Only a correlated delivery notification later moves a document on to Sent.
    transmissionStatus: string;
    destination: string | null;
    attemptedAt: string | null;
    // The transport's receipt. Null unless a real adapter issued one — its absence is what keeps
    // `Sent` unreachable without a transport that can evidence the send.
    externalMessageId: string | null;
    // Our reference for the attempt on the Zen/SES route (SCRUM-1353), echoed only in Zen's response; null on the SMTP route.
    clientReference: string | null;
    // Fixed server-side classification code, never free text.
    failureReason: string | null;
    errors: string[];
}

// One outbound transmission audit entry (SCRUM-927 / §170.315(d)(2)/(d)(3)).
export interface CcdaTransmissionAuditEntryDto {
    ccdaTransmissionId: number;
    ccdaDocumentId: number;
    patientId: number | null;
    // Resolved server-side at read time from the current chart — never stored on the audit row, which keeps
    // free-text PHI out of the trail itself. `providerName` is the ATTENDING provider, not the user who
    // pressed Send; that is `userName`, and on an audit report the two must not be confused.
    patientName: string | null;
    facilityName: string | null;
    providerName: string | null;
    eventType: string;
    timestamp: string | null;
    userId: string;
    userName: string | null;
    documentType: string | null;
    recipientDirectAddress: string | null;
    transport: string | null;
    externalMessageId: string | null;
    // Evidence-integrity flag: a Transmitted entry without a transport receipt cannot be corroborated
    // as a real send, so the report renders it as unverified rather than as a send.
    transportReceiptPresent: boolean;
    failureReason: string | null;
    // Our reference for the attempt on the Zen/SES route (SCRUM-1353); null on the SMTP route.
    clientReference: string | null;
}

// Filters for the org-wide outbound audit report. The inbound report pages only; (d)(3) asks for a
// report over the ASTM E2147 elements — date/time, patient, user, type of action — so this one filters.
export interface CcdaTransmissionAuditFilter {
    fromDate?: string | null;
    toDate?: string | null;
    patientId?: number | null;
    userId?: string | null;
    // One of the three transmission event display values; omit for all three.
    eventType?: string | null;
}

// The three EventLog display values the outbound report renders and filters on.
export const TRANSMISSION_EVENT_TYPES = {
    Transmitted: 'C-CDA Document Transmitted',
    Failed: 'C-CDA Document Transmission Failed',
    Suppressed: 'C-CDA Document Transmission Suppressed (No Transport)',
} as const;

// --- SCRUM-922 inbound Direct fetch over IMAP (§170.315(b)(1) receive) -----------------------------------

// One mailbox read. `status` is deliberately not a union of the three server values: an unrecognized status
// must render as itself rather than be coerced into one we know, because "we could not classify this run" and
// "this run completed" are different statements about whether a clinical document arrived.
export interface CcdaFetchRunDto {
    id: number;
    status: string;
    // The account the fetch authenticated as. Each ETT per-test mailbox is a separate account whose
    // folder is called INBOX, so the folder alone cannot identify the run.
    account: string | null;
    mailboxName: string | null;
    startedAt: string | null;
    completedAt: string | null;
    // Mailbox totals reported by IMAP STATUS (ETT Test 21) — the whole mailbox, not this run.
    messagesInMailbox: number;
    unreadInMailbox: number;
    // What this run actually pulled; bounded server-side by CcdaImap:MaxMessagesPerFetch.
    messagesProcessed: number;
    documentsStaged: number;
    // Messages that failed. Non-zero on a Completed run is expected and correct — one bad message does not
    // stop the batch (AC4).
    errorCount: number;
    failureReason: string | null;
    startedBy: string | null;
}

// One message inside a run. Envelope only — there is no subject and no body, by design on the server: the
// subject can name a patient and the body IS the clinical document.
export interface CcdaFetchMessageDto {
    id: number;
    status: string;
    transportMessageId: string | null;
    senderDirectAddress: string | null;
    sizeBytes: number | null;
    attachmentCount: number;
    documentsStaged: number;
    // Documents skipped because an earlier read of the same message (same Message-Id, same bytes) had already
    // staged them. Explains why a two-document package produced one staging row.
    duplicatesSkipped: number;
    isPackage: boolean;
    // Whether the original XDM package was retained and can be downloaded (AC3).
    packageAvailable: boolean;
    errorCount: number;
    errors: CcdaFetchFindingDto[];
    // ETT IMAP Test 28: the PDF, text or image files kept beside the C-CDA. Listed and openable, never staged.
    attachments: CcdaFetchAttachmentDto[];
}

export interface CcdaFetchAttachmentDto {
    index: number;
    fileName: string | null;
    // The type the server stored the file under, never the sender's label.
    contentType: string;
    sizeBytes: number;
}

// A per-message finding: a fixed server code plus its code-derived message. Never raw server text.
export interface CcdaFetchFindingDto {
    code: string | null;
    message: string | null;
}

export interface CcdaFetchRunDetailDto {
    run: CcdaFetchRunDto;
    messages: CcdaFetchMessageDto[];
}

// Per-run overrides typed by the operator. They exist because the ETT hosts a DIFFERENT mailbox per IMAP
// test, so a certification session switches account between runs — Test 19/20/24 reads imaptesting@, Test 27
// xdmmimetypes@, Test 28 multipleattachments@, Test 29(a) brokenrefstylesheet@, Test 29(b) invalidstylesheet@,
// Test 30 xdmbadxhtml@. Never stored: they live in component state for the length of one request.
export interface CcdaFetchOverrides {
    imapUserName?: string | null;
    imapPassword?: string | null;
    // Comma-separated. ETT IMAP Test 21 reads the folders FOLDER and folder2.
    mailboxNames?: string | null;
}

// The ETT's per-test mailboxes, from the SITE B1 IMAP MORE INFO panels. Offered as a quick pick so an
// operator running the suite picks the TEST rather than retyping an address, which is where a typo would cost
// a run. Eighteen mailboxes, and every one of them shares ETT_IMAP_PASSWORD.
export const ETT_IMAP_MAILBOXES = [
    { label: 'Test 19/20/24 — session, SASL, retrieve', address: 'imaptesting@james.healthit.gov' },
    { label: 'Test 21 — folder names', address: 'imaptesting@james.healthit.gov', mailboxNames: 'FOLDER,folder2' },
    { label: 'Test 25 — size and status updates', address: 'imaptesting@james.healthit.gov' },
    { label: 'Test 27 — XDM, varying MIME types', address: 'xdmmimetypes@james.healthit.gov' },
    { label: 'Test 28 — multiple attachments', address: 'multipleattachments@james.healthit.gov' },
    { label: 'Test 29(a) — broken stylesheet reference', address: 'brokenrefstylesheet@james.healthit.gov' },
    { label: 'Test 29(b) — invalid stylesheet', address: 'invalidstylesheet@james.healthit.gov' },
    { label: 'Test 30 — XDM with bad XHTML', address: 'xdmbadxhtml@james.healthit.gov' },
    // Test 31 is one mailbox per attachment type, matching the tool's own dropdown.
    { label: 'Test 31 — (b)(1) Transitions of Care, Ambulatory', address: 'b1-ambulatory@james.healthit.gov' },
    { label: 'Test 31 — (b)(1) Transitions of Care, Inpatient', address: 'b1-inpatient@james.healthit.gov' },
    { label: 'Test 31 — (b)(2) Reconciliation, Ambulatory', address: 'b2-ambulatory@james.healthit.gov' },
    { label: 'Test 31 — (b)(2) Reconciliation, Inpatient', address: 'b2-inpatient@james.healthit.gov' },
    { label: 'Test 31 — (b)(5) CCDS Summary, Ambulatory', address: 'b5-ambulatory@james.healthit.gov' },
    { label: 'Test 31 — (b)(5) CCDS Summary, Inpatient', address: 'b5-inpatient@james.healthit.gov' },
    { label: 'Test 31 — (b)(9) Care Plan, Ambulatory', address: 'b9-ambulatory@james.healthit.gov' },
    { label: 'Test 31 — (b)(9) Care Plan, Inpatient', address: 'b9-inpatient@james.healthit.gov' },
    { label: 'Test 31 — Negative testing, CCDS', address: 'negativetestingccds@james.healthit.gov' },
    { label: 'Test 31 — Negative testing, Care Plan', address: 'negativetestingcareplan@james.healthit.gov' },
] as const;

// Published by the ETT in the MORE INFO panel of every IMAP test, and shared by all eighteen mailboxes above.
// A public credential for a government test harness — no PHI, no customer system. Pre-filled so the operator
// picks a test and runs; it is still typed into a password field and sent for one request only.
export const ETT_IMAP_PASSWORD = 'smtptesting123';

// Fixed server codes on a failed fetch, rendered as something an operator can act on. An unrecognized code
// falls through to itself rather than to a generic message: inventing prose for a code we do not know would
// describe a failure we cannot actually classify.
export const FETCH_FAILURE_REASONS: Record<string, string> = {
    // §170.315(b)(1) Test Procedure step 3: refusing the connection is the REQUIRED behaviour, so this is a
    // pass being reported, not a defect. The ETT's own certificate is self-signed and names hosts other than
    // the one it publishes, so this is what a fetch against it does by default.
    TlsCertificateRejected: 'Connection refused — the server’s TLS certificate did not validate. This is the required behaviour for an untrusted certificate.',
    AuthenticationRejected: 'The server refused the mailbox account. Check the account and password for this test.',
    HostUnreachable: 'The mail host could not be reached. Check the hostname, the port, and whether outbound traffic on it is blocked.',
    MailboxNotFound: 'The named mailbox does not exist on the server.',
    NoInboxConfigured: 'No mailbox is configured, so nothing was read.',
    MailboxUnreadable: 'The mailbox was opened but the read did not complete. Unread messages stay unread, so fetch again; if it repeats, report the run to support.',
};

export interface CcdaFetchRunFilter {
    fromDate?: string | null;
    toDate?: string | null;
    // One of FETCH_RUN_STATUSES; omit for all.
    status?: string | null;
}

// The three run statuses the history renders and filters on.
export const FETCH_RUN_STATUSES = {
    Running: 'Running',
    Completed: 'Completed',
    Failed: 'Failed',
} as const;

// Per-message outcomes.
export const FETCH_MESSAGE_STATUSES = {
    Staged: 'Staged',
    NoDocuments: 'NoDocuments',
    // Every document had already been staged by an earlier read of the same message — the guard against a
    // second copy of a clinical document. An outcome, not a failure.
    Duplicate: 'Duplicate',
    // The message was a delivery notification (MDN) for something we sent, not an inbound document. It is
    // the (g)(2) RT7 confirmation of receipt, so the log has to show it landing.
    DeliveryReceipt: 'DeliveryReceipt',
    Failed: 'Failed',
} as const;

export interface CcdaReconcileSessionListDto {
    listType: ReconcileListType;
    total: number;
    pending: number;
    accepted: number;
    rejected: number;
    reviewed: boolean;
}

export interface CcdaReconcileSessionDto {
    stagingId: number;
    status: CcdaReceiveStatus;
    receivedDate: string | null;
    documentType: string;
    matchesFound: number;
    suggestedPatientId: number | null;
    resolvedPatientId: number | null;
    documentSourceLabel: string | null;
    lists: CcdaReconcileSessionListDto[];
}

// Staged incoming item from the received document.
export interface ReconcileIncomingItemDto {
    id: number;
    listType: ReconcileListType;
    code: string | null;
    codeSystemOid: string | null;
    displayName: string | null;
    onsetDate: string | null;
    statusText: string | null;
    severity: string | null;
    // AC-2 provenance.
    sourceLabel: string | null;
    sourceLastModified: string | null;
    decision: ReconcileDecision;
    consolidatedIntoItemId: number | null;
    // Backend pairing signal: the chart key this item pairs to (a user PATCH overrides it); null = unpaired.
    matchedExistingKey: string | null;
    // Backend-computed match classification (v4) — render the badge/buttons from this, never diff client-side.
    matchStatus: MatchStatus;
    // Which fields drove a Conflict (currently "status"/"onset"); empty unless matchStatus === Conflict.
    // Used only to highlight the differing fields in the display.
    conflictFields: string[];
    // Optimistic-concurrency token (base64, v4). Echo it back in the PATCH body so a stale write gets a
    // 409 instead of silently clobbering another clinician's decision.
    rowVersion: string;
    // Per-domain extras JSON (all keys optional; parse defensively):
    //   medications: { sig, dose, route:{code,display} }
    //   allergies:   { severity:{display}, reaction:{code,display}, criticality, allergyType, category }
    //   problems:    { severity:{code,display} }  — `severity` above is pre-extracted; JSON is only for the rest.
    sourceAttributesJson?: string | null;
}

// Live chart item (read fresh from the active intake on every request).
export interface ReconcileExistingItemDto {
    key: string; // stable pairing key (currently the SNOMED code)
    code: string | null;
    codeSystemOid: string | null;
    displayName: string | null;
    onsetDate: string | null;
    statusText: string | null;
    severity: string | null;
    sourceLabel: string | null;
    lastModified: string | null;
}

export interface ReconcileListDto {
    listType: ReconcileListType;
    reviewed: boolean;
    incoming: ReconcileIncomingItemDto[];
    existing: ReconcileExistingItemDto[];
}

export interface PatchDecisionBody {
    decision: ReconcileDecision;
    consolidatedIntoItemId?: number | null;
    matchedExistingKey?: string | null;
    // Optimistic-concurrency token from the item being decided (v4) — omit to skip the check.
    rowVersion?: string | null;
}

export interface CareTransitionsState {
    received: CcdaReceiveListItemDto[];
    receivedLoading: boolean;
    // requestId of the latest fetchReceived — lets the reducer drop out-of-order (stale) responses.
    receivedRequestId: string | null;
    // Per-patient received-document audit (SCRUM-921 AC6).
    audit: CcdaReceiveAuditEntryDto[];
    auditLoading: boolean;
    // Org-wide received-document audit report (Admin ▸ System, server-side paged).
    auditPage: CcdaReceiveAuditEntryDto[];
    auditPageLoading: boolean;
    auditPagePagination: CcdaInboxPagination;
    auditPageRequestId: string | null;
    // Per-patient OUTBOUND transmission audit (SCRUM-927) — a separate trail from the inbound one above.
    transmissionAudit: CcdaTransmissionAuditEntryDto[];
    transmissionAuditLoading: boolean;
    // Org-wide outbound transmission audit report (Admin ▸ System, server-side paged + filtered).
    transmissionAuditPage: CcdaTransmissionAuditEntryDto[];
    transmissionAuditPageLoading: boolean;
    transmissionAuditPagePagination: CcdaInboxPagination;
    transmissionAuditPageRequestId: string | null;
    // Document whose transmit is in flight (the Generated list's Send action).
    transmittingDocumentId: number | null;
    // SCRUM-922 inbound fetch history (Admin ▸ System, server-side paged + filtered).
    fetchRuns: CcdaFetchRunDto[];
    fetchRunsLoading: boolean;
    fetchRunsPagination: CcdaInboxPagination;
    fetchRunsFilter: CcdaFetchRunFilter;
    fetchRunsRequestId: string | null;
    // Per-run message detail, keyed by run id — loaded lazily when a history row is expanded.
    fetchRunDetails: Record<number, CcdaFetchMessageDto[]>;
    fetchRunDetailLoadingId: number | null;
    // True while a mailbox read is in flight. Guards the button against a second concurrent run.
    fetching: boolean;
    // The run the last fetch produced, so the screen can report the outcome without re-reading the list.
    lastFetchRun: CcdaFetchRunDto | null;
    // Global inbound admin queue, the C-CDA Exchange Inbound Queue tab (server-side paged/filtered, across all
    // patients incl. unmatched).
    inbox: CcdaReceiveListItemDto[];
    inboxLoading: boolean;
    inboxPagination: CcdaInboxPagination;
    inboxFilter: CcdaInboxFilter;
    // requestId of the latest fetchInbox — lets the reducer drop out-of-order (stale) responses.
    inboxRequestId: string | null;
    session: CcdaReconcileSessionDto | null;
    sessionLoading: boolean;
    // Keyed by ReconcileListType.
    lists: Partial<Record<ReconcileListType, ReconcileListDto>>;
    listLoading: Partial<Record<ReconcileListType, boolean>>;
    savingItemId: number | null;
    confirmingListType: ReconcileListType | null;
    // Document-level coded incorporation in flight (SCRUM-792 commit).
    committing: boolean;
    errorMessage: string | null;
}

export const initialCareTransitionsState: CareTransitionsState = {
    received: [],
    receivedLoading: false,
    receivedRequestId: null,
    audit: [],
    auditLoading: false,
    auditPage: [],
    auditPageLoading: false,
    auditPagePagination: { current: 1, pageSize: 10, total: 0 },
    auditPageRequestId: null,
    transmissionAudit: [],
    transmissionAuditLoading: false,
    transmissionAuditPage: [],
    transmissionAuditPageLoading: false,
    transmissionAuditPagePagination: { current: 1, pageSize: 10, total: 0 },
    transmissionAuditPageRequestId: null,
    transmittingDocumentId: null,
    fetchRuns: [],
    fetchRunsLoading: false,
    fetchRunsPagination: { current: 1, pageSize: 10, total: 0 },
    fetchRunsFilter: {},
    fetchRunsRequestId: null,
    fetchRunDetails: {},
    fetchRunDetailLoadingId: null,
    fetching: false,
    lastFetchRun: null,
    inbox: [],
    inboxLoading: false,
    inboxPagination: { current: 1, pageSize: 10, total: 0 },
    inboxFilter: {},
    inboxRequestId: null,
    session: null,
    sessionLoading: false,
    lists: {},
    listLoading: {},
    savingItemId: null,
    confirmingListType: null,
    committing: false,
    errorMessage: null,
};

export const RECONCILE_LIST_ORDER: ReconcileListType[] = [ReconcileListType.Problem, ReconcileListType.Medication, ReconcileListType.Allergy];

export const reconcileListLabel = (listType: ReconcileListType): string => {
    switch (listType) {
        case ReconcileListType.Problem:
            return 'Problems / Diagnoses';
        case ReconcileListType.Medication:
            return 'Medications';
        case ReconcileListType.Allergy:
            return 'Allergies & Intolerances';
        default:
            return 'Items';
    }
};

// Short label for a coded item's code system OID (best-effort; falls back to a generic label).
const CODE_SYSTEM_LABELS: Record<string, string> = {
    '2.16.840.1.113883.6.96': 'SNOMED',
    '2.16.840.1.113883.6.88': 'RxNorm',
    '2.16.840.1.113883.6.90': 'ICD-10',
    '2.16.840.1.113883.4.9': 'UNII',
    '2.16.840.1.113883.6.1': 'LOINC',
};

export const codeSystemLabel = (oid: string | null | undefined): string => (oid && CODE_SYSTEM_LABELS[oid]) || 'Code';
